import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LoadingButton } from '@mui/lab'
import { Box, CircularProgress, Typography } from '@mui/material'
import { GridColDef, GridRowModel } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { IFilterField } from 'common/AbstractDataGridToolbar/CustomDataGridFilter'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import AbstractDataGridTooltip from 'common/AbstractDataGridTooltip'
import { StatsCards } from 'common/AbstractStatsCards'
import { DisplayTotals } from 'common/AbstractStatsCards'
import ColorBox from 'common/Box/ColorWhiteBox'
import TabPanel from 'common/TabPanel/TabPanel'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'
import { InventoryStatus } from 'types/api/faber/inventory.type'
import { EsimDataType } from 'types/api/faber/inventory.type'

import InventoryAPI from '../../../../api/faber/InventoryAPI'
import EditCellConfirmationDialog from '../components/EditCellConfirmationDialog'

const confirmationMessage = (
  newRow: ESims | GridRowModel,
  oldRow: ESims | GridRowModel
) => {
  return `Are you sure you want to change the status of ${newRow.iccid} from ${oldRow.iccid_status} to ${newRow.iccid_status}?`
}

export default function ESimManagement() {
  const [promiseArguments, setPromiseArguments] =
    useState<IPromiseArguments | null>()
  const faberToken = OrbitSession.get('faberToken')
  const [tabValue] = useState(0)
  const [, setDataArray] = useState<IDataProps[]>([])
  const [esims, setESims] = useState<EsimDataType[]>([])
  const [availableInventory, setAvailableInventory] = useState<EsimDataType[]>(
    []
  )
  const [provisionedInventory, setProvisionedInventory] = useState<
    EsimDataType[]
  >([])
  const [terminatedInventory, setTerminatedInventory] = useState<
    EsimDataType[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totals, setTotals] = useState<DisplayTotals[]>([])

  // for search bar filter
  const [searchValue, setSearchValue] = useState<string>('')
  const [tempSearchValue, setTempSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')
  const [rowCount, setRowCount] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  const [pageOffset, setPageOffset] = useState(0)

  //for filter
  const [statusChecked, setStatusChecked] = useState<string[]>([])
  const [filterActive, setFilterActive] = useState<boolean>(false)

  const searchOptions = ['Email', 'ICCID', 'Customer ID']

  const filterFields = useMemo(() => {
    let tempFilterFields: IFilterField[] = []
    tempFilterFields = [
      {
        values: ['AVAILABLE', 'PROVISIONED', 'TERMINATED'],
        checked: statusChecked,
        setChecked: setStatusChecked,
        name: 'Status',
        index: 'status'
      }
    ]

    return tempFilterFields
  }, [statusChecked])

  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    // CONVERT + TO %2B FOR QUERY URL
    setTempSearchValue(e.target.value.replace('+', '%2B'))
  }
  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPageOffset(tmpOffset)
    setPage(newPage)
  }

  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize,
      searchCriteria,
      searchValue,
      statusChecked
    }),
    [pageOffset, pageSize, searchCriteria, searchValue, statusChecked]
  )

  const { data: inventoryData, isLoading: gettingAllSimsLoading } = useQuery(
    ['getAllInventoryEsimsData', queryOptions],
    () => {
      return InventoryAPI.getEsimInventoryV2(
        pageOffset,
        pageSize,
        faberToken,
        statusChecked,
        searchCriteria,
        tempSearchValue
      )
    },
    {
      onError: () => {
        setIsLoading(false)
      }
    }
  )

  const { mutate: updateInventoryStatus } = useMutation(
    InventoryAPI.updateEsimInventory,
    {
      onSuccess: () => {
        if (promiseArguments) {
          toast.success('Inventory status updated')

          const { newRow, resolve } = promiseArguments

          resolve(newRow)

          setESims((old) => {
            return old.map((esim) => {
              if (esim.matchingId === newRow.matchingId) {
                esim.status = newRow.status
              }
              return esim
            })
          })

          setPromiseArguments(null)
        }
      },
      onError: () => {
        if (promiseArguments) {
          toast.error('Failed to update inventory status')

          const { oldRow, reject } = promiseArguments
          reject(oldRow)

          setPromiseArguments(null)
        }
      }
    }
  )

  const handleUpdateConfirmation = async () => {
    if (promiseArguments) {
      const { newRow } = promiseArguments
      updateInventoryStatus({
        iccid: newRow.iccid,
        status: newRow.iccid_status,
        faberToken: faberToken
      })
    }
  }

  const processRowUpdate = useCallback(
    (newRow: GridRowModel, oldRow: GridRowModel) =>
      new Promise<GridRowModel>((resolve, reject) => {
        const mutation = confirmationMessage(newRow, oldRow)

        if (mutation) {
          setPromiseArguments({ resolve, reject, newRow, oldRow })
        } else {
          resolve(oldRow)
        }
      }),
    []
  )

  const columns: GridColDef[] = [
    { field: 'iccid', headerName: 'ICCID', minWidth: 200, flex: 1.5 },
    { field: 'userId', headerName: 'User ID', minWidth: 200, flex: 1.5 },
    { field: 'userName', headerName: 'User Name', minWidth: 100, flex: 1 },
    { field: 'userEmail', headerName: 'User Email', minWidth: 200, flex: 2 },
    {
      field: 'status',
      headerName: 'ICCID Status',
      editable: true,
      type: 'singleSelect',
      valueOptions: () => [
        InventoryStatus.AVAILABLE,
        InventoryStatus.PROVISIONED,
        InventoryStatus.TERMINATED
      ],
      minWidth: 100
    },
    {
      field: 'telnaStatus',
      headerName: 'Telna Status',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'provisionedAt',
      headerName: 'Provisioned At',
      minWidth: 100,
      valueFormatter: ({ value }) =>
        value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
    },
    { field: 'matchingId', headerName: 'Matching ID', minWidth: 200 },
    { field: 'smdpAddress', headerName: 'SM_DP Address', minWidth: 200 },
    {
      field: 'qrCode',
      headerName: 'Qr Code',
      minWidth: 350,
      flex: 2,
      renderCell: (params: { row: EsimDataType }) => {
        return (
          <AbstractDataGridTooltip>{params.row.qrCode}</AbstractDataGridTooltip>
        )
      }
    }
  ]

  useEffect(() => {
    if (inventoryData) {
      const { data: allInventoryData } = inventoryData

      const { data: esimAllData } = allInventoryData

      setESims(esimAllData.esimsData)
      setRowCount(esimAllData.paginationData.total)
      setIsLoading(false)
    }
  }, [inventoryData, page, searchCriteria, tempSearchValue])

  useEffect(() => {
    if (inventoryData) {
      const { esimsCount } = inventoryData.data.data
      const summary: DisplayTotals[] = esimsCount.map((esim) => {
        return {
          title: `${esim.status.toUpperCase()}`,
          data: [`${esim.count}`]
        }
      })
      setTotals(summary)
    }
  }, [inventoryData])

  useEffect(() => {
    const available = esims.filter((esim: EsimDataType) => {
      return esim.status === InventoryStatus.AVAILABLE
    })

    const provisioned = esims.filter((esim: EsimDataType) => {
      return esim.status === InventoryStatus.PROVISIONED
    })

    const terminated = esims.filter((esim: EsimDataType) => {
      return esim.status === InventoryStatus.TERMINATED
    })

    setAvailableInventory(available)
    setProvisionedInventory(provisioned)
    setTerminatedInventory(terminated)

    setDataArray([
      { title: 'All', info: esims.length },
      { title: 'Available', info: available.length },
      {
        title: 'Provisioned',
        info: provisioned.length
      },
      {
        title: 'Terminated',
        info: terminated.length
      }
    ])
  }, [esims])

  /* TODO: REMOVE WHEN CONIRM NO LONGER NEEDED */
  // const variantPicker = (allocatedNum) =>
  //   allocatedNum === tabValue ? 'filled' : 'tertiary'
  // const colorPicker = (allocatedNum) =>
  //   allocatedNum === tabValue ? 'white' : 'black'

  const navigate = useNavigate();

  return !isLoading ? (
    <>
      <ColorBox
        sx={{mb:'20px'}}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LoadingButton
          variant='outlined'
          component='label'
          sx={{ fontSize: '12px', p: 3, background: 'white' }}
          startIcon={<FileUploadIcon />}
          loadingIndicator={<CircularProgress />}
          onClick={() => {navigate('../manage-inventory-faber/upload')}} 
        >
          Upload eSims Script
        </LoadingButton>
          {/* Warning Text */}
          <Typography variant="body2" color="warning.main" sx={{ fontSize: '12px' }}>
            ⚠️ Please ensure the script matches the latest schema and contains no more than 15,000 records.
          </Typography>
        </Box>
      </ColorBox>
      <StatsCards totals={totals} bad={true} />

      <ColorBox>
        {/* TODO: REMOVE IF NO LONGER NECESSARY */}
        {/* <Grid item>
        <ColorBoxContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
          >
            {dataArray.map((label, index) => {
              return (
                <TabButton
                  key={label.title}
                  label={`${label.title} (${label.info})`}
                  color={colorPicker(index)}
                  variant={variantPicker(index)}
                  value={index}
                  onClick={(num) => setTabValue(num)}
                />
              )
            })}
          </Tabs>
        </ColorBoxContent>
      </Grid> */}
        <>
          <EditCellConfirmationDialog
            {...{
              promiseArguments,
              setPromiseArguments,
              dialogTitle: 'Are you sure?',
              confirmationMessage,
              dialogConfirmation: handleUpdateConfirmation
            }}
          />
          {[
            esims,
            availableInventory,
            provisionedInventory,
            terminatedInventory
          ].map((inventory, index) => {
            return (
              <TabPanel
                key={`ESimsManagementPage-tab${index}`}
                value={tabValue}
                index={index}
              >
                <MUIDataGrid
                  getRowId={(esim: EsimDataType) =>
                    esim.userId + esims.indexOf(esim) + esim.iccid
                  }
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'createdAt', sort: 'desc' }]
                    }
                  }}
                  tableHeight='700px'
                  rows={inventory}
                  pagination
                  rowCount={rowCount}
                  page={page}
                  onPageChange={handlePageChange}
                  pageSize={pageSize}
                  columns={columns}
                  processRowUpdate={processRowUpdate}
                  experimentalFeatures={{ newEditingApi: true }}
                  components={{ Toolbar: Toolbar }}
                  disableColumnFilter
                  loading={gettingAllSimsLoading}
                  sortingOrder={['desc', 'asc']}
                  disableColumnMenu={false}
                  paginationMode='server'
                  componentsProps={{
                    toolbar: {
                      serverFilterProps: {
                        filterFields,
                        filterActive,
                        setFilterActive
                      },
                      searchBar: 'server',
                      serverSearchBarProps: {
                        onSubmit: onSearchSubmit,
                        onChange: onSearchValueChange,
                        options: searchOptions,
                        defaultValue: searchCriteria,
                        handleChange: handleSearchCriteriaChange
                      },
                      filter: 'server'
                    }
                  }}
                />
              </TabPanel>
            )
          })}
        </>
      </ColorBox>
    </>
  ) : (
    <CircularProgress />
  )
}

interface ESims {
  id: string
  iccid: string
  matching_id: string
  qr_code: string
  smdp_address: string
  user_id: string
  user_email: string
  user_name: string
  iccid_status: string
  telna_status: string
  provisioned_at: Date
}

interface IPromiseArguments {
  newRow: GridRowModel
  oldRow: GridRowModel
  resolve: (row: GridRowModel) => void
  reject: (row: GridRowModel) => void
}

interface IDataProps {
  title: string
  info: number
}
